import { Box, type BoxProps } from "@mui/material";
import {
  DataGridPro,
  type DataGridProProps,
  type GridToolbarProps,
  GridToolbarQuickFilter,
  type GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { memo, useRef } from "react";
import { type Except } from "type-fest";

import { type ColumnData, type RowData } from "~types/datagrid";

import { PrintWidget } from "../../atoms/DataGrid/Widgets/PrintWidget";
import {
  EmptyState,
  type EmptyStateProps,
  ErrorState,
  type ErrorStateProps,
  ListLoadingState,
  type ListLoadingStateProps,
} from "../../atoms/SectionStates";

const MIN_HEIGHT: React.CSSProperties["height"] = "372px";

interface ToolbarCustomProps {
  widgetOptions?: { show?: boolean; showPrintWidget?: boolean };
}

function getToolbar({ widgetOptions = {} }: ToolbarCustomProps) {
  const { show = true, showPrintWidget = true } = widgetOptions;

  return function Toolbar({ showQuickFilter, quickFilterProps, ...props }: GridToolbarProps) {
    return (
      <Box
        sx={{
          "& .MuiFormControl-root": {
            width: "100%",
          },
          alignItems: { sm: "center", xs: "stretch" },
          display: "flex",
          flexDirection: { sm: "row", xs: "column" },
          gap: 2,
          justifyContent: "space-between",
          maxWidth: "100%",
          minWidth: 0, // Allow toolbar to shrink inside flex/grid parents without widening the page
          pb: { sm: 2, xs: 6 }, // Extra bottom padding on mobile to separate from table header
          pt: 2,
          px: 1,
          width: "100%",
        }}
      >
        {Boolean(showQuickFilter) && (
          <Box sx={{ flex: { sm: "1 1 auto", xs: "0 0 100%" }, maxWidth: { sm: 400 }, minWidth: 0 }}>
            <GridToolbarQuickFilter {...quickFilterProps} />
          </Box>
        )}
        {show && (
          <>
            {!props.children && <i />}
            <Box
              sx={{
                display: "flex",
                flex: { sm: "1 1 0%", xs: "0 0 auto" },
                justifyContent: { sm: "flex-end", xs: "flex-start" },
                maxWidth: "100%",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "inline-flex",
                  flexWrap: "wrap",
                  gap: 1,
                  justifyContent: { sm: "flex-end", xs: "flex-start" },
                  maxWidth: "100%",
                  minWidth: 0,
                }}
              >
                {props.children}
                {showPrintWidget && <PrintWidget />}
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  };
}

const sharedSlotProps: ListLoadingStateProps["slotProps"] = {
  container: {
    height: `calc(${MIN_HEIGHT} - 2px)`,
    sx: {
      border: "none",
      borderRadius: "0.25rem",
    },
  },
};

export type DataGridBaseProps<T extends GridValidRowModel> = Except<
  DataGridProProps<RowData<T>>,
  "columns" | "rows"
> & {
  /** Column data. This should be memoized. */
  columns: readonly ColumnData<T>[];
  /** Row data. This should be memoized. */
  rows: readonly RowData<T>[];
  headerWidgets?: React.ReactNode;
  status?: Utils.QueryStatus;
  toolbarCustomProps?: ToolbarCustomProps;
  rowHeight?: number | "auto" | string;
};
export type DataGridSlotProps<T extends GridValidRowModel> = Pick<
  DataGridProProps<RowData<T>>,
  "slotProps" | "slots"
> & {
  slotProps?: {
    container?: BoxProps;
    empty?: EmptyStateProps;
    error?: ErrorStateProps;
    loading?: ListLoadingStateProps;
  };
  slots?: {
    empty?: React.ReactNode;
    error?: React.ReactNode;
    loading?: React.ReactNode;
  };
};
export type DataGridProps<T extends GridValidRowModel> = Except<DataGridBaseProps<T>, "slotProps" | "slots"> &
  DataGridSlotProps<T>;

function NoRowsOverlayWrapper(props?: EmptyStateProps) {
  return function NoRowsOverlay() {
    return <EmptyState slotProps={sharedSlotProps} {...props} />;
  };
}

export function DataGrid<T extends GridValidRowModel>({
  headerWidgets,
  status = "success",
  toolbarCustomProps = {},
  rowHeight = "auto" as unknown as number,
  ...props
}: DataGridProps<T>) {
  const ref = useRef<HTMLDivElement>(null);

  const noRowsOverlay = memo(
    NoRowsOverlayWrapper({
      ...props.slotProps?.empty,
      slotProps: {
        ...props.slotProps?.empty?.slotProps,
        container: {
          ...props.slotProps?.empty?.slotProps?.container,
          height: "100%",
        },
      },
    }),
  );

  return (
    <Box
      {...props.slotProps?.container}
      sx={{
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        width: "100%",
        ...props.slotProps?.container?.sx,
      }}
    >
      {status === "error" &&
        (props.slots?.error ?? <ErrorState slotProps={sharedSlotProps} {...props.slotProps?.error} />)}
      {status === "pending" &&
        (props.slots?.loading ?? (
          <ListLoadingState count={7} slotProps={sharedSlotProps} {...props.slotProps?.loading} />
        ))}
      {status === "success" && (
        /**
         * We receive the following typescript error;
         *
         * @example
         *  Types of property 'type' are incompatible.
              Type 'GridColType | undefined' is not assignable to type '"singleSelect"'.
                Type 'undefined' is not assignable to type '"singleSelect"'.ts(2322)
         */
        // @ts-expect-error `type` is allowed to be `undefined` according to `node_modules/@mui/x-data-grid/models/colDef/gridColDef.d.ts`
        <DataGridPro<T>
          disableColumnFilter
          filterMode="client"
          getRowHeight={() => (typeof rowHeight === "number" ? rowHeight : "auto")}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          pagination
          {...props}
          getRowId={(row) => row.rowId}
          initialState={{
            ...props.initialState,
            pagination: {
              ...props.initialState?.pagination,
              paginationModel: { pageSize: 5, ...props.initialState?.pagination?.paginationModel },
            },
          }}
          ref={ref}
          slotProps={{
            ...props.slotProps,
            footer: {
              ...props.slotProps?.footer,
              sx: { display: "flex", justifyContent: "center", ...props.slotProps?.footer?.sx },
            },
            toolbar: {
              children: headerWidgets,
              showQuickFilter: true,
              ...props.slotProps?.toolbar,
              quickFilterProps: {
                debounceMs: 333,
                fullWidth: true,
                size: "medium",
                sx: {
                  "& .MuiInputBase-root": {
                    pl: 2,
                    width: "100%",
                  },
                },
                ...props.slotProps?.toolbar?.quickFilterProps,
              },
              style: {
                paddingBottom: 0,
                ...props.slotProps?.toolbar?.style,
              },
            },
          }}
          slots={{ noRowsOverlay, toolbar: getToolbar(toolbarCustomProps), ...props.slots }}
          componentsProps={{
            basePopper: {
              // Fix for ARIA role issues in the DataGrid
              slotProps: {
                root: {
                  role: "presentation",
                },
              },
            },
          }}
          sx={{
            " .MuiDataGrid-cell": { alignItems: "center", padding: "0.5rem" },
            " .MuiDataGrid-columnHeader": {
              " .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
              "&:focus-within": { outline: "unset" },
            },
            " .MuiDataGrid-root": {
              maxWidth: "100%",
              minWidth: 0,
              width: "100%",
            },
            " .MuiDataGrid-toolbarContainer": {
              boxSizing: "border-box",
              maxWidth: "100%",
              minWidth: 0,
              padding: "0",
              paddingRight: "1rem", // Prevent button cutoff
            },
            // Do not force row/header width to 100% here: that fixes flex-column grids (mui/mui-x#3098)
            // but creates a bogus horizontal scrollbar on fixed-width column grids (e.g. DistrictRsvps).
            "& .MuiDataGrid-main": { minWidth: 0, width: "100%" },
            "& .MuiDataGrid-virtualScroller": { minWidth: 0, width: "100% !important" },
            ...(props.rows?.length
              ? {}
              : {
                  // Empty grid: avoid `overflow: hidden` on the scroller — it clips the no-rows overlay and
                  // crushes the default empty message. Reserve height so the overlay sits above the footer.
                  "& .MuiDataGrid-overlayWrapper": {
                    display: "grid !important",
                    minHeight: 240,
                    minWidth: 0,
                    placeItems: "center",
                    width: "100% !important",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    minHeight: 240,
                    minWidth: 0,
                    width: "100% !important",
                  },
                }),
            border: "none",
            boxSizing: "border-box",
            maxWidth: "100%",
            minWidth: 0, // Allow grid to shrink in flex/grid contexts
            width: "100%",
            ...props.sx,
          }}
        />
      )}
    </Box>
  );
}
